import { useCallback, useEffect, useRef, useState } from 'react';
import { LogOut, RefreshCw, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { RecordButton } from '@/components/RecordButton';
import { ChatBubble, TypingBubble } from '@/components/ChatBubble';
import { DayHistory, loadDayEntries, saveDayEntry } from '@/components/DayHistory';
import { useRecorder } from '@/hooks/useRecorder';
import { STATES, BUSY_STATES } from '@/lib/states';
import * as api from '@/lib/api';

const CONFIRM_TEXT = 'Sí, confirmo que la información es correcta. Calcula los carbohidratos.';
const REJECT_TEXT = 'No, la información no es correcta. Te la voy a repetir.';

export default function MainScreen({ onLogout }) {
  const [state, setState] = useState(STATES.IDLE);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [dayEntries, setDayEntries] = useState(loadDayEntries);
  const historyRef = useRef([]);
  const retryRef = useRef(null);
  const bottomRef = useRef(null);

  const pushMessage = useCallback((msg) => setMessages((prev) => [...prev, msg]), []);

  const fail = useCallback((message, retryFn) => {
    setError(message);
    retryRef.current = retryFn || null;
    setState(STATES.ERROR);
  }, []);

  const doChat = useCallback(async (text, { calculating = false } = {}) => {
    setState(calculating ? STATES.CALCULATING : STATES.THINKING);
    let resp;
    try {
      resp = await api.chat(historyRef.current, text);
    } catch (err) {
      fail(api.apiErrorMessage(err, 'No se pudo contactar al asistente. Revisa tu conexión.'), () => doChat(text, { calculating }));
      return;
    }
    historyRef.current = [
      ...historyRef.current,
      { role: 'user', content: text },
      { role: 'assistant', content: resp.assistant_message },
    ];
    pushMessage({ role: 'assistant', text: resp.message, foods: resp.foods || [], kind: resp.status, total: resp.total_g });
    if (resp.status === 'confirm') {
      setState(STATES.CONFIRMING);
    } else if (resp.status === 'result') {
      setState(STATES.RESULT);
      if (resp.total_g != null) {
        const summary = (resp.foods || []).map((f) => f.name).join(', ') || 'Comida';
        setDayEntries(saveDayEntry({
          time: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
          summary,
          total: resp.total_g,
        }));
      }
    } else {
      setState(STATES.CLARIFY);
    }
  }, [fail, pushMessage]);

  const sendToChat = useCallback((text, { calculating = false, displayText } = {}) => {
    pushMessage({ role: 'user', text: displayText || text });
    doChat(text, { calculating });
  }, [doChat, pushMessage]);

  const handleRecordingComplete = useCallback(async (blob, mimeType) => {
    setState(STATES.TRANSCRIBING);
    let text;
    try {
      text = await api.transcribe(blob, mimeType);
    } catch (err) {
      fail(api.apiErrorMessage(err, 'No se pudo transcribir el audio. Revisa tu conexión.'), () => handleRecordingComplete(blob, mimeType));
      return;
    }
    sendToChat(text);
  }, [fail, sendToChat]);

  const handleRecorderError = useCallback((message) => {
    fail(message, () => startRecording()); // eslint-disable-line no-use-before-define
  }, [fail]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isRecording, seconds, start, stop } = useRecorder({
    onComplete: handleRecordingComplete,
    onError: handleRecorderError,
  });

  const startRecording = useCallback(async () => {
    setError('');
    const ok = await start();
    if (ok) setState(STATES.RECORDING);
  }, [start]);

  const handleRecordToggle = () => {
    if (isRecording) stop();
    else startRecording();
  };

  const handleNewCalc = () => {
    if (isRecording) stop();
    historyRef.current = [];
    retryRef.current = null;
    setMessages([]);
    setError('');
    setState(STATES.IDLE);
  };

  const handleRetry = () => {
    setError('');
    if (retryRef.current) retryRef.current();
    else setState(STATES.IDLE);
  };

  // Keep the conversation scrolled to the latest message.
  useEffect(() => {
    if (messages.length === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, state]);

  const busy = BUSY_STATES.includes(state);
  const recordDisabled = busy || state === STATES.CONFIRMING;
  const recordDisabledReason = busy
    ? 'Espera a que termine el procesamiento'
    : state === STATES.CONFIRMING
      ? 'Primero responde si la información es correcta'
      : '';
  const canNewCalc = !isRecording && !busy && (messages.length > 0 || state !== STATES.IDLE);
  // Big button only before the chat starts; small (no resize animation) once it does.
  const compact = messages.length > 0;

  return (
    <div className="min-h-dvh bg-background flex flex-col" data-testid="main-screen">
      <header className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-lg mx-auto w-full px-4 py-3 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-primary tracking-tight">Carbi</h1>
          <Button variant="outline" size="sm" onClick={onLogout} className="gap-1.5 rounded-full" data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Salir
          </Button>
        </div>
      </header>

      <div className="sticky top-[57px] z-10 flex justify-center py-2 bg-background/80 backdrop-blur">
        <StatusBadge state={state} />
      </div>

      <main className={`flex-1 w-full max-w-lg mx-auto px-4 pt-1 flex flex-col gap-3 transition-all ${compact ? 'pb-36' : 'pb-56'}`}>
        {messages.length === 0 && state === STATES.IDLE && (
          <>
            <div className="text-center py-10">
              <p className="text-xl font-semibold text-foreground">Presiona Grabar y dime qué comiste</p>
              <p className="text-base text-muted-foreground mt-2">
                Incluye porciones y detalles si puedes, por ejemplo: &ldquo;dos tacos de pastor y un vaso de agua de horchata&rdquo;.
              </p>
            </div>
            <DayHistory entries={dayEntries} />
          </>
        )}

        {messages.length > 0 && (
          <div className="flex flex-col gap-3" data-testid="chat-feed">
            {messages.map((msg, i) => <ChatBubble key={i} msg={msg} />)}
            {busy && <TypingBubble />}
          </div>
        )}

        {state === STATES.CONFIRMING && (
          <div className="grid grid-cols-2 gap-3 max-w-[85%]" data-testid="confirmation-actions">
            <Button
              className="h-13 py-3 rounded-xl text-base font-semibold gap-2"
              onClick={() => sendToChat(CONFIRM_TEXT, { calculating: true, displayText: 'Sí, es correcto' })}
              data-testid="confirm-yes-button"
            >
              <Check className="h-5 w-5" /> Sí, confirmar
            </Button>
            <Button
              variant="outline"
              className="h-13 py-3 rounded-xl text-base font-semibold gap-2 bg-card"
              onClick={() => sendToChat(REJECT_TEXT, { displayText: 'No, voy a corregirlo' })}
              data-testid="confirm-no-button"
            >
              <X className="h-5 w-5" /> No, corregir
            </Button>
          </div>
        )}

        {state === STATES.CLARIFY && (
          <p className="text-sm text-muted-foreground pl-1" data-testid="clarify-hint">Presiona Grabar para responder.</p>
        )}

        {state === STATES.ERROR && (
          <Card className="border-destructive/40 bg-destructive/5" data-testid="error-card">
            <CardContent className="py-4">
              <p className="text-base text-destructive font-medium" data-testid="error-message">{error}</p>
              <Button
                variant="destructive"
                className="mt-3 h-12 rounded-xl text-base font-semibold gap-2 w-full"
                onClick={handleRetry}
                data-testid="retry-button"
              >
                <RefreshCw className="h-5 w-5" /> Reintentar
              </Button>
            </CardContent>
          </Card>
        )}

        <div ref={bottomRef} />
      </main>

      <footer className="fixed bottom-0 inset-x-0 z-20 bg-background/95 backdrop-blur border-t border-border safe-area-pb">
        <div className={`max-w-lg mx-auto w-full px-4 ${compact ? 'pt-2 pb-1' : 'pt-4 pb-2'}`}>
          {compact ? (
            <div className="flex items-center justify-center gap-4">
              <RecordButton
                compact
                isRecording={isRecording}
                seconds={seconds}
                disabled={recordDisabled}
                disabledReason={recordDisabledReason}
                onToggle={handleRecordToggle}
              />
              <Button
                variant="outline"
                className="h-11 rounded-xl text-sm font-semibold px-6"
                disabled={!canNewCalc}
                title={!canNewCalc ? 'Disponible cuando haya un cálculo en curso' : ''}
                onClick={handleNewCalc}
                data-testid="new-calc-button"
              >
                Nuevo cálculo
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <RecordButton
                isRecording={isRecording}
                seconds={seconds}
                disabled={recordDisabled}
                disabledReason={recordDisabledReason}
                onToggle={handleRecordToggle}
              />
              <Button
                variant="outline"
                className="h-12 w-full rounded-xl text-base font-semibold"
                disabled={!canNewCalc}
                title={!canNewCalc ? 'Disponible cuando haya un cálculo en curso' : ''}
                onClick={handleNewCalc}
                data-testid="new-calc-button"
              >
                Nuevo cálculo
              </Button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}
